import Editor, { type BeforeMount, type OnMount } from "@monaco-editor/react";
import { Play, X } from "lucide-react";
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { findDocumentScript } from "@/document/model/documentScripts";
import { documentStore } from "@/editor/store";
import { useStoreSubscribe } from "@/lib/store";
import { ensureMonacoConfigured } from "./monacoSetup";
import { resizeObserverBorderBoxSize } from "./resizeObserverGeometry";

ensureMonacoConfigured();

interface ScriptEditorWindowProps {
  scriptId: string;
  onChangeScript: (scriptId: string, source: string) => void;
  onRenameScript: (scriptId: string, name: string) => void;
  onRunScript: (scriptId: string) => void | Promise<void>;
  onClose: () => void;
}

interface PanelGeometry {
  x: number;
  y: number;
  width: number;
  height: number;
}

const storageKey = "curva:script-editor-window";
const viewportMargin = 12;
const minPanelWidth = 420;
const minPanelHeight = 280;
const dragThresholdPx = 4;

export function ScriptEditorWindow({
  scriptId,
  onChangeScript,
  onRenameScript,
  onRunScript,
  onClose,
}: ScriptEditorWindowProps) {
  const document = useStoreSubscribe(documentStore.document);
  const script = findDocumentScript(document.scripts, scriptId);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    panelX: number;
    panelY: number;
    dragging: boolean;
  } | undefined>(undefined);
  const [geometry, setGeometry] = useState<PanelGeometry>(() => initialPanelGeometry());
  const [running, setRunning] = useState(false);
  const [renaming, setRenaming] = useState(false);

  useEffect(() => {
    setRenaming(false);
  }, [scriptId]);

  useEffect(() => {
    if (!script) {
      onClose();
    }
  }, [script, onClose]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      const borderBox = resizeObserverBorderBoxSize(entry);
      const width = Math.max(minPanelWidth, borderBox.width);
      const height = Math.max(minPanelHeight, borderBox.height);
      setGeometry(current => {
        if (Math.abs(current.width - width) < 0.5 && Math.abs(current.height - height) < 0.5) {
          return current;
        }
        const next = clampPanelGeometry({ ...current, width, height });
        storePanelGeometry(next);
        return next;
      });
    });

    observer.observe(panel);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setGeometry(current => {
        const next = clampPanelGeometry(current);
        storePanelGeometry(next);
        return next;
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function beginRename(): void {
    dragRef.current = undefined;
    setRenaming(true);
  }

  function handleHeaderPointerDown(event: ReactPointerEvent<HTMLDivElement>): void {
    if (event.button !== 0 || renaming || event.detail > 1) return;
    // Delay capture / preventDefault until the pointer actually moves so
    // double-click rename on the title can still fire.
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      panelX: geometry.x,
      panelY: geometry.y,
      dragging: false,
    };
  }

  function handleHeaderPointerMove(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    if (!drag.dragging) {
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      if (dx * dx + dy * dy < dragThresholdPx * dragThresholdPx) {
        return;
      }
      drag.dragging = true;
      event.currentTarget.setPointerCapture(event.pointerId);
    }

    const next = clampPanelGeometry({
      ...geometry,
      x: drag.panelX + event.clientX - drag.startX,
      y: drag.panelY + event.clientY - drag.startY,
    });
    setGeometry(next);
  }

  function handleHeaderPointerUp(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = undefined;
    if (drag.dragging) {
      storePanelGeometry(geometry);
    }
  }

  if (!script) {
    return null;
  }

  return (
    <section
      ref={panelRef}
      data-curva-script-editor=""
      className="fixed z-[1000] flex min-h-[280px] min-w-[420px] resize overflow-hidden rounded-md border border-white/10 bg-[#171b20] shadow-2xl shadow-black/45"
      style={{ left: geometry.x, top: geometry.y, width: geometry.width, height: geometry.height }}
      aria-label="Document script editor"
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div
          className={`flex h-8 shrink-0 items-center justify-between border-b border-white/10 bg-[#20262d] px-2 text-[12px] font-medium text-slate-300 ${renaming ? "cursor-default" : "cursor-move"}`}
          onPointerDown={handleHeaderPointerDown}
          onPointerMove={handleHeaderPointerMove}
          onPointerUp={handleHeaderPointerUp}
          onPointerCancel={handleHeaderPointerUp}
        >
          {renaming ? (
            <ScriptTitleRenameInput
              name={script.name}
              onCommit={name => {
                setRenaming(false);
                onRenameScript(scriptId, name);
              }}
              onCancel={() => setRenaming(false)}
            />
          ) : (
            <>
              <button
                type="button"
                className="max-w-[calc(100%-5.5rem)] shrink truncate text-left font-medium text-slate-300 hover:text-slate-100"
                title="Double-click to rename"
                onPointerDown={event => event.stopPropagation()}
                onDoubleClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  beginRename();
                }}
              >
                Script — {script.name}
              </button>
              <div className="min-h-full min-w-2 flex-1" aria-hidden="true" />
            </>
          )}
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              className="flex h-6 items-center gap-1 rounded px-1.5 text-[11px] text-slate-300 hover:bg-white/[0.08] hover:text-slate-100 disabled:opacity-50"
              aria-label="Run script"
              disabled={running}
              onPointerDown={event => event.stopPropagation()}
              onClick={() => {
                setRunning(true);
                void Promise.resolve(onRunScript(scriptId)).finally(() => setRunning(false));
              }}
            >
              <Play className="size-3.5" />
              Run
            </button>
            <button
              type="button"
              className="flex size-6 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-white/[0.08] hover:text-slate-100"
              aria-label="Close script editor"
              onPointerDown={event => event.stopPropagation()}
              onClick={onClose}
            >
              <X className="size-3.5" />
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-hidden">
          <Editor
            key={scriptId}
            height="100%"
            language="luau"
            theme="vs-dark"
            value={script.source}
            path={`inmemory://curva/script/${scriptId}.luau`}
            beforeMount={configureLuauLanguage}
            onMount={focusEditor}
            onChange={value => onChangeScript(scriptId, value ?? "")}
            options={{
              automaticLayout: true,
              fontSize: 13,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              wordWrap: "on",
              tabSize: 2,
              renderLineHighlight: "line",
              padding: { top: 8, bottom: 8 },
            }}
          />
        </div>
      </div>
    </section>
  );
}

function ScriptTitleRenameInput({
  name,
  onCommit,
  onCancel,
}: {
  name: string;
  onCommit: (name: string) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(name);
  const trimmedDraft = draft.trim();
  return (
    <input
      className="min-w-0 flex-1 rounded-sm border border-sky-400/70 bg-[#0f1318] px-1 py-0 text-[12px] font-medium text-slate-50 outline-none"
      value={draft}
      autoFocus
      aria-label="Rename script"
      onFocus={event => event.currentTarget.select()}
      onChange={event => setDraft(event.target.value)}
      onPointerDown={event => event.stopPropagation()}
      onDoubleClick={event => event.stopPropagation()}
      onBlur={() => onCommit(trimmedDraft || name)}
      onKeyDown={event => {
        if (event.key === "Enter") {
          event.preventDefault();
          onCommit(trimmedDraft || name);
        } else if (event.key === "Escape") {
          event.preventDefault();
          onCancel();
        }
      }}
    />
  );
}

const configureLuauLanguage: BeforeMount = monaco => {
  if (monaco.languages.getLanguages().some(language => language.id === "luau")) {
    return;
  }

  monaco.languages.register({ id: "luau", aliases: ["Luau"], extensions: [".luau"] });
  monaco.languages.setLanguageConfiguration("luau", {
    comments: { lineComment: "--", blockComment: ["--[[", "]]"] },
    brackets: [
      ["{", "}"],
      ["[", "]"],
      ["(", ")"],
    ],
    autoClosingPairs: [
      { open: "{", close: "}" },
      { open: "[", close: "]" },
      { open: "(", close: ")" },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
      { open: "`", close: "`" },
    ],
    surroundingPairs: [
      { open: "{", close: "}" },
      { open: "[", close: "]" },
      { open: "(", close: ")" },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
      { open: "`", close: "`" },
    ],
    indentationRules: {
      increaseIndentPattern: /^\s*(function|then|do|repeat|else|elseif)\b.*$/,
      decreaseIndentPattern: /^\s*(end|until|else|elseif)\b/,
    },
  });
  monaco.languages.setMonarchTokensProvider("luau", {
    defaultToken: "",
    tokenPostfix: ".luau",
    keywords: [
      "and", "break", "continue", "do", "else", "elseif", "end", "export", "false", "for",
      "function", "if", "in", "local", "nil", "not", "or", "repeat", "return", "then",
      "true", "type", "typeof", "until", "while",
    ],
    operators: [
      "+", "-", "*", "/", "%", "^", "#", "==", "~=", "<=", ">=", "<", ">", "=",
      ";", ":", ",", ".", "..", "...", "+=", "-=", "*=", "/=", "%=", "^=", "..=",
      "->", "::", "?",
    ],
    symbols: /[=><!~?:&|+\-*/^%#]+/,
    escapes: /\\(?:[abfnrtv\\"']|x[0-9A-Fa-f]{1,4}|u\{[0-9A-Fa-f]+\}|[0-7]{1,3})/,
    tokenizer: {
      root: [
        [/[a-zA-Z_]\w*/, {
          cases: {
            "@keywords": "keyword",
            "@default": "identifier",
          },
        }],
        { include: "@whitespace" },
        [/[{}()[\]]/, "@brackets"],
        [/@symbols/, {
          cases: {
            "@operators": "operator",
            "@default": "",
          },
        }],
        [/\d*\.\d+([eE][-+]?\d+)?/, "number.float"],
        [/0[xX][0-9a-fA-F_]+/, "number.hex"],
        [/\d+/, "number"],
        [/[;,.]/, "delimiter"],
        [/"([^"\\]|\\.)*$/, "string.invalid"],
        [/'([^'\\]|\\.)*$/, "string.invalid"],
        [/"/, "string", "@string_double"],
        [/'/, "string", "@string_single"],
        [/`/, "string", "@string_interp"],
      ],
      whitespace: [
        [/[ \t\r\n]+/, ""],
        [/--\[\[/, "comment", "@comment"],
        [/--.*$/, "comment"],
      ],
      comment: [
        [/[^\]]+/, "comment"],
        [/\]\]/, "comment", "@pop"],
        [/./, "comment"],
      ],
      string_double: [
        [/[^\\"]+/, "string"],
        [/@escapes/, "string.escape"],
        [/\\./, "string.escape.invalid"],
        [/"/, "string", "@pop"],
      ],
      string_single: [
        [/[^\\']+/, "string"],
        [/@escapes/, "string.escape"],
        [/\\./, "string.escape.invalid"],
        [/'/, "string", "@pop"],
      ],
      string_interp: [
        [/[^\\`{]+/, "string"],
        [/@escapes/, "string.escape"],
        [/\\./, "string.escape.invalid"],
        [/\{/, { token: "delimiter.bracket", next: "@interp" }],
        [/`/, "string", "@pop"],
      ],
      interp: [
        [/\}/, { token: "delimiter.bracket", next: "@pop" }],
        { include: "root" },
      ],
    },
  });
};

const focusEditor: OnMount = editor => {
  editor.focus();
};

function initialPanelGeometry(): PanelGeometry {
  const stored = readStoredPanelGeometry();
  if (stored) {
    return clampPanelGeometry(stored);
  }
  const width = Math.min(640, Math.max(minPanelWidth, window.innerWidth * 0.45));
  const height = Math.min(480, Math.max(minPanelHeight, window.innerHeight * 0.55));
  return clampPanelGeometry({
    x: Math.max(viewportMargin, window.innerWidth - width - 48),
    y: Math.max(viewportMargin, 72),
    width,
    height,
  });
}

function clampPanelGeometry(geometry: PanelGeometry): PanelGeometry {
  const width = Math.min(Math.max(minPanelWidth, geometry.width), Math.max(minPanelWidth, window.innerWidth - viewportMargin * 2));
  const height = Math.min(Math.max(minPanelHeight, geometry.height), Math.max(minPanelHeight, window.innerHeight - viewportMargin * 2));
  return {
    width,
    height,
    x: Math.min(Math.max(viewportMargin, geometry.x), Math.max(viewportMargin, window.innerWidth - width - viewportMargin)),
    y: Math.min(Math.max(viewportMargin, geometry.y), Math.max(viewportMargin, window.innerHeight - height - viewportMargin)),
  };
}

function readStoredPanelGeometry(): PanelGeometry | undefined {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<PanelGeometry>;
    if (![parsed.x, parsed.y, parsed.width, parsed.height].every(value => typeof value === "number" && Number.isFinite(value))) {
      return undefined;
    }
    return parsed as PanelGeometry;
  } catch {
    return undefined;
  }
}

function storePanelGeometry(geometry: PanelGeometry): void {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(geometry));
  } catch {
    // Ignore quota / private mode failures.
  }
}
